import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Building2, BarChart3, Activity, Search, Filter, Download, Plus, Edit2, Trash2, X, ChevronDown, AlertCircle, CheckCircle, Menu } from 'lucide-react';

const AdminPanel = ({ session, supabase }) => {
  // Estados principais
  const [tab, setTab] = useState('acoesrapidas');
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [stats, setStats] = useState({ totalUsers: 0, totalCompanies: 0, adminUsers: 0, companiesWithUsers: 0 });
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState({ type: null, data: null });
  const [form, setForm] = useState({});
  const [feedback, setFeedback] = useState({ type: '', message: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Carregar dados
  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const { count: totalUsers } = await supabase.from('profiles').select('*', { count: 'exact' });
        const { count: totalCompanies } = await supabase.from('companies').select('*', { count: 'exact' });
        
        setStats({ 
          totalUsers: totalUsers || 0, 
          totalCompanies: totalCompanies || 0, 
          totalAnalyses: 0, 
          activeUsers: 0 
        });

        const { data: usersData } = await supabase.from('profiles').select(`*, company:company_id (nome_fantasia)`);
        setUsers(usersData || []);
        const { data: companiesData } = await supabase.from('companies').select('*');
        setCompanies(companiesData || []);
      } catch (err) {
        setFeedback({ type: 'error', message: err.message });
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [supabase]);

  // Handlers CRUD
  const openModal = (type, data = null) => {
    setModal({ type, data });
    setForm(data ? { ...data } : {});
    setFeedback({ type: '', message: '' });
  };
  
  const closeModal = () => {
    setModal({ type: null, data: null });
    setForm({});
    setFeedback({ type: '', message: '' });
  };
  
  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const saveUser = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = (await supabase.auth.getSession()).data.session.access_token;
      
      if (modal.data) {
        // Atualizar usuário existente via API
        const updateData = {
          nome_completo: form.nome_completo,
          company_id: form.company_id,
          role: form.role
        };
        
        // Se senha foi fornecida, inclui no update
        if (form.password && form.password.trim() !== '') {
          updateData.password = form.password;
        }
        
        const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/users/${modal.data.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(updateData)
        });
        
        if (!response.ok) {
          const err = await response.json();
          throw new Error(err.detail || 'Erro ao atualizar usuário');
        }
        
        setFeedback({ type: 'success', message: 'Usuário atualizado com sucesso!' });
      } else {
        // Criar novo usuário via API
        const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/create-user`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            email: form.email,
            password: form.password,
            nome_completo: form.nome_completo,
            company_id: form.company_id,
            role: form.role
          })
        });
        if (!response.ok) {
          const err = await response.json();
          throw new Error(err.detail || 'Erro ao cadastrar usuário');
        }
        setFeedback({ type: 'success', message: 'Usuário cadastrado com sucesso!' });
      }
      closeModal();
      window.location.reload();
    } catch (err) {
      setFeedback({ type: 'error', message: err.message });
    } finally {
      setLoading(false);
    }
  };

const deleteUser = async id => {
  if (!window.confirm('Tem certeza que deseja excluir este usuário? Esta ação não pode ser desfeita e removerá o acesso completo ao sistema.')) return;
  setLoading(true);
  try {
    const token = (await supabase.auth.getSession()).data.session.access_token;
    
    const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/users/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Erro ao excluir usuário');
    }

    setFeedback({ type: 'success', message: 'Usuário excluído completamente do sistema!' });
    
    // Atualiza a lista de usuários sem recarregar a página
    const updatedUsers = users.filter(u => u.id !== id);
    setUsers(updatedUsers);
    
    // Atualiza as estatísticas
    setStats(prev => ({ ...prev, totalUsers: prev.totalUsers - 1 }));
    
  } catch (err) {
    setFeedback({ type: 'error', message: err.message });
  } finally {
    setLoading(false);
  }
};

  const saveCompany = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      if (modal.data) {
        const { error } = await supabase.from('companies').update({ 
          razao_social: form.razao_social, 
          nome_fantasia: form.nome_fantasia, 
          cnpj: form.cnpj,
          numero_operadora: form.numero_operadora 
        }).eq('id', modal.data.id);
        if (error) throw error;
        setFeedback({ type: 'success', message: 'Empresa atualizada com sucesso!' });
      } else {
        const { error } = await supabase.from('companies').insert([{ 
          razao_social: form.razao_social, 
          nome_fantasia: form.nome_fantasia, 
          cnpj: form.cnpj,
          numero_operadora: form.numero_operadora 
        }]);
        if (error) throw error;
        setFeedback({ type: 'success', message: 'Empresa cadastrada com sucesso!' });
      }
      closeModal();
      window.location.reload();
    } catch (err) {
      setFeedback({ type: 'error', message: err.message });
    } finally {
      setLoading(false);
    }
  };

  const deleteCompany = async id => {
  // Primeiro, busca quantos usuários estão vinculados
  const usersInCompany = users.filter(u => u.company_id === id);
  const userCount = usersInCompany.length;
  
  // Monta mensagem de confirmação personalizada
  let confirmMessage = 'Tem certeza que deseja excluir esta empresa?';
  
  if (userCount > 0) {
    const userNames = usersInCompany.map(u => u.nome_completo || u.email).join(', ');
    confirmMessage = `⚠️ ATENÇÃO: Esta empresa possui ${userCount} usuário(s) vinculado(s):\n\n${userNames}\n\nTODOS esses usuários serão PERMANENTEMENTE REMOVIDOS do sistema, incluindo seu acesso ao login.\n\nEsta ação NÃO PODE ser desfeita.\n\nDeseja continuar?`;
  } else {
    confirmMessage += ' Esta ação não pode ser desfeita.';
  }
  
  if (!window.confirm(confirmMessage)) return;
  
  setLoading(true);
  try {
    const token = (await supabase.auth.getSession()).data.session.access_token;
    
    const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/companies/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Erro ao excluir empresa');
    }

    const result = await response.json();
    
    setFeedback({ 
      type: 'success', 
      message: result.message || 'Empresa e usuários vinculados excluídos com sucesso!'
    });
    
    // Atualiza listas localmente sem recarregar página
    const updatedCompanies = companies.filter(c => c.id !== id);
    setCompanies(updatedCompanies);
    
    const updatedUsers = users.filter(u => u.company_id !== id);
    setUsers(updatedUsers);
    
    // Atualiza estatísticas
    setStats(prev => ({
      ...prev,
      totalCompanies: prev.totalCompanies - 1,
      totalUsers: prev.totalUsers - userCount
    }));
    
  } catch (err) {
    setFeedback({ type: 'error', message: err.message });
  } finally {
    setLoading(false);
  }
};

  // Filtros e busca
  const filteredUsers = users
    .filter(user => {
      const matchesSearch = user.nome_completo?.toLowerCase().includes(searchTerm.toLowerCase()) || 
                           user.email?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesRole = filterRole === 'all' || user.role === filterRole;
      return matchesSearch && matchesRole;
    })
    .sort((a, b) => {
      if (sortBy === 'name') return (a.nome_completo || '').localeCompare(b.nome_completo || '');
      if (sortBy === 'email') return (a.email || '').localeCompare(b.email || '');
      return 0;
    });

  const filteredCompanies = companies.filter(company => 
    company.nome_fantasia?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    company.razao_social?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    company.cnpj?.includes(searchTerm)
  );

  // Cards de estatísticas
  const StatCard = ({ icon: Icon, title, value, subtitle, color }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow p-4 sm:p-6 border border-gray-100"
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-xs sm:text-sm font-medium text-gray-600 mb-1">{title}</p>
          <p className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1">{value}</p>
          {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
        </div>
        <div className={`p-2 sm:p-3 rounded-lg ${color}`}>
          <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
        </div>
      </div>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-7xl mx-auto p-4 sm:p-6">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-4xl font-bold text-gray-900 mb-2">Painel Administrativo</h1>
          <p className="text-sm sm:text-base text-gray-600">Gerencie usuários, empresas e visualize estatísticas do sistema</p>
        </div>

        {/* Navegação */}
        <div className="bg-white rounded-xl shadow-sm p-2 mb-6 flex gap-2 overflow-x-auto">
          <button
            onClick={() => setTab('acoesrapidas')}
            className={`flex items-center gap-2 px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-semibold transition-all whitespace-nowrap ${
              tab === 'acoesrapidas'
                ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-md'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            <span className="text-sm sm:text-base">Ações Rápidas</span>
          </button>
          <button
            onClick={() => setTab('users')}
            className={`flex items-center gap-2 px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-semibold transition-all whitespace-nowrap ${
              tab === 'users'
                ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-md'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            <Users className="w-4 h-4 sm:w-5 sm:h-5" />
            <span className="text-sm sm:text-base">Usuários</span>
          </button>
          <button
            onClick={() => setTab('companies')}
            className={`flex items-center gap-2 px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-semibold transition-all whitespace-nowrap ${
              tab === 'companies'
                ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-md'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            <Building2 className="w-4 h-4 sm:w-5 sm:h-5" />
            <span className="text-sm sm:text-base">Empresas</span>
          </button>
        </div>

        {/* Feedback */}
        <AnimatePresence>
          {feedback.message && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className={`mb-6 px-4 sm:px-6 py-3 sm:py-4 rounded-xl font-medium flex items-center gap-3 shadow-md text-sm sm:text-base ${
                feedback.type === 'error'
                  ? 'bg-red-50 text-red-700 border border-red-200'
                  : 'bg-green-50 text-green-700 border border-green-200'
              }`}
            >
              {feedback.type === 'error' ? <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" /> : <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />}
              <span className="break-words">{feedback.message}</span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Dashboard */}
        {tab === 'acoesrapidas' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
            {/* Quick Actions */}
            <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 border border-gray-100">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Ações Rápidas</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                <button
                  onClick={() => openModal('user')}
                  className="flex items-center gap-3 p-3 sm:p-4 rounded-lg border-2 border-dashed border-gray-300 hover:border-blue-500 hover:bg-blue-50 transition-all group"
                >
                  <div className="p-2 bg-blue-100 rounded-lg group-hover:bg-blue-200 transition-colors flex-shrink-0">
                    <Plus className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                  </div>
                  <div className="text-left">
                    <p className="font-semibold text-gray-900 text-sm sm:text-base">Adicionar Usuário</p>
                    <p className="text-xs sm:text-sm text-gray-500">Cadastrar novo usuário</p>
                  </div>
                </button>
                <button
                  onClick={() => openModal('company')}
                  className="flex items-center gap-3 p-3 sm:p-4 rounded-lg border-2 border-dashed border-gray-300 hover:border-purple-500 hover:bg-purple-50 transition-all group"
                >
                  <div className="p-2 bg-purple-100 rounded-lg group-hover:bg-purple-200 transition-colors flex-shrink-0">
                    <Plus className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
                  </div>
                  <div className="text-left">
                    <p className="font-semibold text-gray-900 text-sm sm:text-base">Adicionar Empresa</p>
                    <p className="text-xs sm:text-sm text-gray-500">Cadastrar nova empresa</p>
                  </div>
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Usuários */}
        {tab === 'users' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-white rounded-xl shadow-sm border border-gray-100">
            {/* Header com busca e filtros */}
            <div className="p-4 sm:p-6 border-b border-gray-200">
            <div className="flex flex-col gap-4">
                <div>
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Usuários</h2>
                <p className="text-xs sm:text-sm text-gray-500 mt-1">
                    {filteredUsers.length} usuário(s) encontrado(s)
                </p>
                </div>

                {/* Pesquisa + botão lado a lado */}
                <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                {/* Pesquisa */}
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                    <input
                    type="text"
                    placeholder="Buscar usuários..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9 sm:pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full text-sm sm:text-base"
                    />
                </div>

                {/* Botão Novo Usuário */}
                <button
                    onClick={() => openModal("user")}
                    className="flex items-center justify-center gap-2 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-3 sm:px-5 py-2 rounded-lg font-semibold shadow-md transition-all whitespace-nowrap text-sm sm:text-base"
                >
                    <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
                    <span className="hidden sm:inline">Novo Usuário</span>
                    <span className="sm:hidden">Novo Usuário</span>
                </button>
                </div>
            </div>
            </div>


            {/* Tabela Desktop */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Nome</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Email</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Empresa</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Perfil</th>
                    <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Ações</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-semibold mr-3">
                            {user.nome_completo?.charAt(0).toUpperCase() || 'U'}
                          </div>
                          <div className="text-sm font-medium text-gray-900">{user.nome_completo}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{user.email}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{user.company?.nome_fantasia || '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                          user.role === 'admin'
                            ? 'bg-purple-100 text-purple-700'
                            : 'bg-green-100 text-green-700'
                        }`}>
                          {user.role === 'admin' ? 'Administrador' : 'Usuário'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => openModal('user', user)}
                          className="text-blue-600 hover:text-blue-800 mx-2 p-2 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Editar"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => deleteUser(user.id)}
                          className="text-red-600 hover:text-red-800 mx-2 p-2 hover:bg-red-50 rounded-lg transition-colors"
                          title="Excluir"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Cards Mobile */}
            <div className="md:hidden divide-y divide-gray-200">
              {filteredUsers.map((user) => (
                <div key={user.id} className="p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-semibold flex-shrink-0">
                      {user.nome_completo?.charAt(0).toUpperCase() || 'U'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 truncate">{user.nome_completo}</div>
                      <div className="text-sm text-gray-600 truncate">{user.email}</div>
                      <div className="text-sm text-gray-500 mt-1">{user.company?.nome_fantasia || '-'}</div>
                      <div className="mt-2">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${
                          user.role === 'admin'
                            ? 'bg-purple-100 text-purple-700'
                            : 'bg-green-100 text-green-700'
                        }`}>
                          {user.role === 'admin' ? 'Administrador' : 'Usuário'}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <button
                        onClick={() => openModal('user', user)}
                        className="text-blue-600 hover:text-blue-800 p-2 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Editar"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => deleteUser(user.id)}
                        className="text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded-lg transition-colors"
                        title="Excluir"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Empresas */}
        {tab === 'companies' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-white rounded-xl shadow-sm border border-gray-100">
            <div className="p-4 sm:p-6 border-b border-gray-200">
              <div className="flex flex-col gap-4">
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Empresas</h2>
                  <p className="text-xs sm:text-sm text-gray-500 mt-1">{filteredCompanies.length} empresa(s) encontrada(s)</p>
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Buscar empresas..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9 sm:pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full text-sm sm:text-base"
                    />
                  </div>
                  <button
                    onClick={() => openModal('company')}
                    className="flex items-center justify-center gap-2 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-3 sm:px-5 py-2 rounded-lg font-semibold shadow-md transition-all text-sm sm:text-base"
                  >
                    <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
                    Nova Empresa
                  </button>
                </div>
              </div>
            </div>

            {/* Tabela Desktop */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Razão Social</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Nome Fantasia</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">CNPJ</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Usuários</th>
                    <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Ações</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredCompanies.map((company) => (
                    <tr key={company.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center text-white font-semibold mr-3">
                            <Building2 className="w-5 h-5" />
                          </div>
                          <div className="text-sm font-medium text-gray-900">{company.razao_social}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{company.nome_fantasia}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 font-mono">{company.cnpj}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${
                            users.filter(u => u.company_id === company.id).length > 0
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                            {users.filter(u => u.company_id === company.id).length} usuário(s)
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => openModal('company', company)}
                          className="text-blue-600 hover:text-blue-800 mx-2 p-2 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Editar"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => deleteCompany(company.id)}
                          className="text-red-600 hover:text-red-800 mx-2 p-2 hover:bg-red-50 rounded-lg transition-colors"
                          title="Excluir"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Cards Mobile */}
            <div className="md:hidden divide-y divide-gray-200">
              {filteredCompanies.map((company) => (
                <div key={company.id} className="p-4 hover:bg-gray-50 transition-colors">
<div className="flex items-start gap-3">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center text-white flex-shrink-0">
                      <Building2 className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 break-words">{company.razao_social}</div>
                      <div className="text-sm text-gray-600 mt-1 break-words">{company.nome_fantasia}</div>
                      <div className="text-sm text-gray-500 font-mono mt-1 break-all">{company.cnpj}</div>
                      <div className="text-sm text-gray-500 mt-1">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${
                            users.filter(u => u.company_id === company.id).length > 0
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                            {users.filter(u => u.company_id === company.id).length} usuário(s)
                        </span>
                        </div>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <button
                        onClick={() => openModal('company', company)}
                        className="text-blue-600 hover:text-blue-800 p-2 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Editar"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => deleteCompany(company.id)}
                        className="text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded-lg transition-colors"
                        title="Excluir"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Modal */}
        <AnimatePresence>
          {modal.type && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
              onClick={closeModal}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-8 py-4 sm:py-6 rounded-t-2xl z-10">
                  <div className="flex justify-between items-center">
                    <h3 className="text-xl sm:text-2xl font-bold text-gray-900">
                      {modal.type === 'user'
                        ? modal.data ? 'Editar Usuário' : 'Novo Usuário'
                        : modal.data ? 'Editar Empresa' : 'Nova Empresa'}
                    </h3>
                    <button
                      onClick={closeModal}
                      className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                <form onSubmit={modal.type === 'user' ? saveUser : saveCompany} className="p-4 sm:p-8 space-y-4 sm:space-y-5">
                  {modal.type === 'user' ? (
                    <>
                      {!modal.data && (
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Email *</label>
                          <input
                            type="email"
                            name="email"
                            value={form.email || ''}
                            onChange={handleChange}
                            required
                            className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm sm:text-base"
                            placeholder="usuario@exemplo.com"
                          />
                        </div>
                      )}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          {modal.data ? 'Nova Senha (deixe em branco para manter)' : 'Senha *'}
                        </label>
                        <input
                          type="password"
                          name="password"
                          value={form.password || ''}
                          onChange={handleChange}
                          required={!modal.data}
                          className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm sm:text-base"
                          placeholder="••••••••"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Nome Completo *</label>
                        <input
                          type="text"
                          name="nome_completo"
                          value={form.nome_completo || ''}
                          onChange={handleChange}
                          required
                          className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm sm:text-base"
                          placeholder="João da Silva"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Empresa *</label>
                        <select
                          name="company_id"
                          value={form.company_id || ''}
                          onChange={handleChange}
                          required
                          className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm sm:text-base"
                        >
                          <option value="">Selecione uma empresa</option>
                          {companies.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.nome_fantasia}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Perfil *</label>
                        <select
                          name="role"
                          value={form.role || 'user'}
                          onChange={handleChange}
                          required
                          className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm sm:text-base"
                        >
                          <option value="user">Usuário</option>
                          <option value="admin">Administrador</option>
                        </select>
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Razão Social *</label>
                        <input
                          type="text"
                          name="razao_social"
                          value={form.razao_social || ''}
                          onChange={handleChange}
                          required
                          className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm sm:text-base"
                          placeholder="Empresa LTDA"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Nome Fantasia *</label>
                        <input
                          type="text"
                          name="nome_fantasia"
                          value={form.nome_fantasia || ''}
                          onChange={handleChange}
                          required
                          className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm sm:text-base"
                          placeholder="Empresa"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">CNPJ *</label>
                        <input
                          type="text"
                          name="cnpj"
                          value={form.cnpj || ''}
                          onChange={handleChange}
                          required
                          className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all font-mono text-sm sm:text-base"
                          placeholder="00.000.000/0000-00"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Número da Operadora *</label>
                        <input
                          type="text"
                          name="numero_operadora"
                          value={form.numero_operadora || ''}
                          onChange={handleChange}
                          required
                          maxLength="6"
                          className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all font-mono text-sm sm:text-base"
                          placeholder="123456"
                        />
                        <p className="mt-1 text-xs text-gray-500">6 dígitos do registro ANS</p>
                      </div>
                    </>
                  )}
                  
                  <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={closeModal}
                      className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-3 text-gray-700 hover:bg-gray-100 font-semibold rounded-lg transition-colors text-sm sm:text-base order-2 sm:order-1"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-lg transition-all shadow-md disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base order-1 sm:order-2"
                    >
                      {loading ? 'Salvando...' : modal.data ? 'Atualizar' : 'Cadastrar'}
                    </button>
                  </div>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default AdminPanel;